import { Component, Inject, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ToastNotificationService } from '@app/core/infrastructure';
import { passwordMatchValidator } from '@app/utils/validators.utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, take } from 'rxjs/operators';
import { LoggerService } from '@app/core/infrastructure';
import { AuthFacade } from '@app/store/auth/auth.facade';
import { UserApiService } from '@app/core/api/user';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { SharedModule } from '@app/shared/modules/shared.module';

export interface ChangePasswordDialogData {
  title?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialSharedModule,
    SharedModule
  ]
})
export class ChangePasswordDialogComponent implements OnInit {
  private readonly logger = inject(LoggerService).scope('ChangePasswordDialogComponent');
  
  changePasswordForm?: FormGroup;
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  isFederatedUser = signal<boolean | undefined>(undefined); // undefined initially, true/false after check
  hideOldPassword = signal(true);
  hideNewPassword = signal(true);
  hideConfirmPassword = signal(true);

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangePasswordDialogData,
    private fb: FormBuilder,
    private authFacade: AuthFacade,
    private toastNotificationService: ToastNotificationService,
    private userApiService: UserApiService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Fetch auth providers from backend to check if user has cognito (email) auth
    this.userApiService.getAuthProviders()
      .pipe(
        take(1),
        untilDestroyed(this)
      )
      .subscribe({
        next: (response) => {
          // Check if user has cognito provider - if yes, they can change password
          const hasCognitoAuth = response.providers.some(p => p.provider === 'cognito');
          this.isFederatedUser.set(!hasCognitoAuth);

          if (!this.isFederatedUser()) {
            this.initForm();
          }
        },
        error: (err) => {
          this.logger.error('Error getting auth providers:', err);
          this.errorMessage.set('Could not load authentication information. Please try again later.');
          this.isFederatedUser.set(true); // Safer default: don't allow password change if we can't verify
        }
      });

    // Subscribe to loading state
    this.authFacade.isPasswordChangeLoading$
      .pipe(untilDestroyed(this))
      .subscribe(isLoading => {
        this.isLoading.set(isLoading);
      });

    // Subscribe to password change errors
    this.authFacade.passwordChangeError$
      .pipe(
        filter(error => error !== null),
        untilDestroyed(this)
      )
      .subscribe(error => {
        this.errorMessage.set(error);
        this.toastNotificationService.showError(error);
      });
  }

  private initForm(): void {
    this.changePasswordForm = this.fb.group({
      oldPassword: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validators: passwordMatchValidator });
  }

  onSubmit(): void {
    if (this.isFederatedUser()) {
      this.toastNotificationService.showInfo(this.translate.instant('toast.passwordManagedByProvider'));
      return;
    }

    if (!this.changePasswordForm || this.changePasswordForm.invalid) {
      this.errorMessage.set('Please fill in all required fields correctly.');
      if (this.changePasswordForm) {
        Object.values(this.changePasswordForm.controls).forEach(control => {
          control.markAsTouched();
        });
      }
      return;
    }

    this.errorMessage.set(null);

    const { oldPassword, password } = this.changePasswordForm.value;

    // Dispatch password change action
    this.authFacade.changePassword(oldPassword, password);

    // Wait for success to close dialog
    this.authFacade.isPasswordChangeLoading$
      .pipe(
        filter(isLoading => !isLoading),
        take(1),
        untilDestroyed(this)
      )
      .subscribe(() => {
        // Check if there's an error
        this.authFacade.passwordChangeError$
          .pipe(take(1))
          .subscribe(error => {
            if (!error) {
              // Success - show toast and close dialog
              this.toastNotificationService.showInfo(this.translate.instant('toast.passwordChangedSuccess'));
              this.dialogRef.close(true);
            }
            // Error is already handled by the error subscription in ngOnInit
          });
      });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
